import {
  ActionIcon,
  Group,
  SegmentedControl,
  Switch,
  Tooltip,
  useMantineColorScheme,
} from '@mantine/core';
import { IconMoon, IconSun, IconSunMoon } from '@tabler/icons-react';
import { forwardRef } from 'react';
import { usePreferencesStore, type ColorSchemeMode } from '../../../stores';

export type ThemeSwitcherVariant = 'icon' | 'switch' | 'segmented';

export interface ThemeSwitcherProps {
  variant?: ThemeSwitcherVariant;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

const ThemeSwitcher = forwardRef<HTMLDivElement, ThemeSwitcherProps>(
  ({ variant = 'icon', size = 'md', showLabel = false }, ref) => {
    const { colorScheme, setColorScheme } = useMantineColorScheme();
    const colorSchemeMode = usePreferencesStore((state) => state.colorSchemeMode);
    const setColorSchemeMode = usePreferencesStore((state) => state.setColorSchemeMode);

    const handleToggle = () => {
      const nextScheme = colorScheme === 'dark' ? 'light' : 'dark';
      setColorScheme(nextScheme);
      setColorSchemeMode(nextScheme);
    };

    const handleModeChange = (value: string) => {
      const mode = value as ColorSchemeMode;
      setColorSchemeMode(mode);
      if (mode !== 'auto') {
        setColorScheme(mode);
      }
    };

    if (variant === 'icon') {
      return (
        <Tooltip label={colorScheme === 'dark' ? 'Light mode' : 'Dark mode'}>
          <ActionIcon
            ref={ref as React.Ref<HTMLButtonElement>}
            variant="subtle"
            size={size}
            onClick={handleToggle}
            aria-label="Toggle color scheme"
          >
            {colorScheme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
          </ActionIcon>
        </Tooltip>
      );
    }

    if (variant === 'switch') {
      return (
        <Group ref={ref} gap="xs">
          <IconSun size={16} opacity={colorScheme === 'light' ? 1 : 0.5} />
          <Switch
            checked={colorScheme === 'dark'}
            onChange={handleToggle}
            size={size}
          />
          <IconMoon size={16} opacity={colorScheme === 'dark' ? 1 : 0.5} />
        </Group>
      );
    }

    return (
      <SegmentedControl
        ref={ref}
        value={colorSchemeMode}
        onChange={handleModeChange}
        size={size}
        data={[
          {
            value: 'light',
            label: (
              <Group gap={4} justify="center">
                <IconSun size={14} />
                {showLabel && <span>Light</span>}
              </Group>
            ),
          },
          {
            value: 'dark',
            label: (
              <Group gap={4} justify="center">
                <IconMoon size={14} />
                {showLabel && <span>Dark</span>}
              </Group>
            ),
          },
          {
            value: 'auto',
            label: (
              <Group gap={4} justify="center">
                <IconSunMoon size={14} />
                {showLabel && <span>Auto</span>}
              </Group>
            ),
          },
        ]}
      />
    );
  }
);

ThemeSwitcher.displayName = 'ThemeSwitcher';

export default ThemeSwitcher;
